<template>
    <UTabs
        v-model="active"
        :items="items"
        color="neutral"
        variant="link"
        :ui="{
            trigger: 'grow',
            root: 'h-full overflow-hidden',
            content: 'overflow-auto'
        }"
    >
        <template #assignment>
            <div v-if="assignment" class="flex flex-col gap-4 pt-4">
                <div class="grid grid-cols-2 gap-2 text-sm">
                    <div class="font-bold">Oppgave</div>
                    <div class="text-right">{{ assignment.assignmentNo }} - {{ assignment.title }}</div>
                    <div class="font-bold">Dato opprettet</div>
                    <div class="text-right">{{ formattedDate(assignment.createdAt) }}</div>
                    <div class="font-bold">Kategori</div>
                    <div class="text-right">{{ cachedT(`assignment.types.${assignment.type}`) }}</div>
                    <span class="font-bold">Status</span>
                    <status-badge :status="assignment.status" :deleted-at="assignment.deletedAt ?? undefined" class="w-fit ml-auto"/>
                    <template v-if="assignment.resourceLinks?.length > 0">
                        <span class="font-bold">Koblinger</span>
                        <div class="flex flex-col gap-1">
                            <div v-for="link in visibleLinks" :key="link.identifier" class="text-right">
                                <resource-link :urn="link">{{ link.display }}</resource-link>
                            </div>
                            <div v-if="hiddenLinksCount > 0 && !showAllLinks" class="text-right">
                                <UButton
                                    variant="outline"
                                    color="neutral"
                                    size="xs"
                                    :label="hiddenLinksCount === 1 ? 'vis +1' : `vis alle +${hiddenLinksCount}`"
                                    @click="showAllLinks = true"
                                />
                            </div>
                            <div v-if="showAllLinks" class="text-right">
                                <UButton
                                    variant="outline"
                                    color="neutral"
                                    size="xs"
                                    label="vis mindre"
                                    @click="showAllLinks = false"
                                />
                            </div>
                        </div>
                    </template>
                </div>

                <USeparator class="mt-4"/>
                <div class="text-sm font-bold">{{ cachedT(`assignment.types.${assignment.type}`) }}</div>
                <component :is="resolveDetailsComponent(assignment)" :actionable="assignment.context" :assignment-uuid="assignment.uuid"/>

                <div v-if="canAutoBook" class="mt-4">
                    <UButton
                        label="Autobokfør"
                        icon="i-ph-lightning"
                        color="primary"
                        :loading="isBooking"
                        @click="handleAutoBook"
                    />
                    <div v-if="bookingError" class="mt-2 text-sm text-red-600">
                        {{ bookingError }}
                    </div>
                </div>
            </div>
        </template>

        <template #pdf>
            <document-preview-multiple :document-meta-uuids="documents.map(d => d.uuid)"/>
        </template>

        <template #json>
            <div>
                <div class="font-bold text-sm">EHF-detaljer</div>
                <ehf-details :json="jsonData" class="py-4" />
                <component :is="resolveDetailsComponent(assignment)" v-if="assignment" :actionable="assignment.context" />
            </div>
        </template>

        <template #xml>
            <pre class="font-mono text-sm w-full h-full overflow-auto whitespace-pre-wrap break-all p-4 bg-gray-50 text-gray-800">
                {{ xmlData }}
            </pre>
        </template>
    </UTabs>
</template>

<script setup lang="ts">

import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {Assignment, ActionableType, AssignmentStatus, AssignmentType} from '@/assignments/Assignment.type'
import {getDocumentMeta} from '@/services/infrastructure/storage.service'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {fetchEhfJSON, fetchEhfXML} from '@/ehf/ehf.service'
import DocumentPreviewMultiple from '@/document/DocumentPreviewMultiple.vue'
import EhfDetails from '@/document/EhfDetails.vue'
import StatusBadge from '@/assignments/StatusBadge.vue'
import {formattedDate} from '@/services/formatter.service'
import {resolveDetailsComponent} from '@/assignments/details/ActionableDetailsResolver'
import ResourceLink from '@/resource-link/ResourceLink.vue'
import {useCachedT} from '@/services/useCachedI18n'
import {resumeAssignment} from '@/assignments/assignment.service'
import {notifySuccess} from '@/services/infrastructure/notification.service'
import axios from 'axios'

const props = defineProps({
    assignment: Object as () => Assignment,
})

const emit = defineEmits(['updated'])
const jsonData = ref()
const xmlData = ref()
const active = ref('0')

const documents = ref<DocumentMeta[]>([])

const {cachedT} = useCachedT()

const isBooking = ref(false)
const bookingError = ref<string | null>(null)

const canAutoBook = computed(() => {
    const a = props.assignment
    if (!a) return false
    if (a.status !== AssignmentStatus.Pending) return false
    if (a.type !== AssignmentType.BookkeepDocument && a.type !== AssignmentType.BookkeepIncomingEhf) return false
    if (a.context?.type !== ActionableType.Bookkeepable) return false
    return a.context.transactionEntryLines.length > 0
})

const handleAutoBook = async () => {
    if (!props.assignment) return
    isBooking.value = true
    bookingError.value = null
    try {
        await resumeAssignment(props.assignment.uuid)
        notifySuccess({title: 'Bokført', description: 'Oppgaven ble automatisk bokført'})
        emit('updated')
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 422) {
            bookingError.value = error.response.data?.detail ?? 'Oppgaven kan ikke bokføres automatisk'
        } else {
            bookingError.value = 'Noe gikk galt ved automatisk bokføring'
        }
    } finally {
        isBooking.value = false
    }
}

const showAllLinks = ref(false)
const visibleLinks = computed(() => showAllLinks.value
    ? (props.assignment?.resourceLinks ?? [])
    : (props.assignment?.resourceLinks?.slice(0, 3) ?? [])
)
const hiddenLinksCount = computed(() => Math.max(0, (props.assignment?.resourceLinks?.length ?? 0) - 3))

const items = computed(() => {
    const tabs = [
        {
            label: 'Oppgave',
            slot: 'assignment'
        }
    ]

    if (documents.value.length > 0) {
        tabs.push({
            label: 'Vedlegg',
            slot: 'pdf'
        })
    }

    if (jsonData.value) {
        tabs.push({
            label: 'Faktura',
            slot: 'json'
        })
    }

    if (xmlData.value) {
        tabs.push({
            label: 'XML',
            slot: 'xml'
        })
    }

    return tabs
})

const populateData = () => {
    if(props.assignment?.context?.type === ActionableType.Bookkeepable) {
        if(!props.assignment) return

        props.assignment.context.resourceLinks.map(async (rl) => {
            if(rl.system === 'ehf') {
                const ehfInvoiceUuid = rl.identifier
                await fetchEhfJSON(ehfInvoiceUuid).then(json => {
                    jsonData.value = json
                })

                await fetchEhfXML(ehfInvoiceUuid).then(xml => {
                    xmlData.value = xml
                })
            }
        })

        props.assignment.context.documentMetaUuids.map(async (uuid) => {
            const docMeta = await getDocumentMeta(uuid)
            if(docMeta) documents.value.push(docMeta)
        })
    }

    props.assignment?.resourceLinks
        ?.filter(rl => rl.type === 'document')
        .map(async (rl) => {
            const docMeta = await getDocumentMeta(rl.identifier)
            if (docMeta) documents.value.push(docMeta)
        })
}

const clearData = () => {
    jsonData.value = null
    xmlData.value = null
    documents.value = []
    active.value = '0'
    showAllLinks.value = false
}

onMounted(async () => {
    populateData()
})

watch(() => props.assignment, async () => {
    clearData()
    populateData()
})

onUnmounted(() => {
    clearData()
})

</script>

<style scoped>

</style>